// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const { CapsuleButton } = require('client-v2/common/components/buttons')
const LazyImage = require('client-v2/common/components/lazy-image')
const Modal = require('client-v2/common/components/modal')
const ArticleModelRecord = require('client-v2/common/models/article/record')
// Sibling imports
const CollectionListItem = require('./components/collection-list-item')
const CollectionListItemRecord = require('./records/collection-list-item')

const MAX_COLLECTION_NAME_LENGTH = 50

const bem = BemEntity('CollectionSelectModal')

class CollectionSelectModal extends React.PureComponent {
  constructor(props) {
    super(props)

    this.filterInputRef = React.createRef()

    this.onFilterChange = this.onFilterChange.bind(this)
    this.onCollectionClick = this.onCollectionClick.bind(this)
    this.onCreateClick = this.onCreateClick.bind(this)
  }

  // Event listeners

  onFilterChange() {
    const inputEl = this.filterInputRef.current
    this.props.onFilterTextChange(inputEl.value)
  }

  onCollectionClick(collection) {
    this.props.onCollectionClick({ collection })
  }

  onCreateClick(event) {
    event.preventDefault()
    this.props.onCreateClick()
  }

  onSupportClick(event) {
    if (typeof Intercom === 'function') {
      // eslint-disable-next-line no-undef
      Intercom('show')
      event.preventDefault()
    }
  }

  // Render methods

  renderHeader() {
    return (
      tag('div', {
        className: bem.element('header')
      },
        tag('div', {
          className: bem.element('title')
        },
          'Add to your collections'
        )
      )
    )
  }

  renderArticle() {
    const { article } = this.props
    if (!article) {
      return null
    }
    return (
      tag('div', {
        className: bem.element('article')
      },
        tag(LazyImage, {
          className: bem.element('article-image'),
          sources: [
            article.md5image,
            article.image
          ]
        }),
        tag('div', {
          className: bem.element('article-details')
        },
          tag('div', {
            className: bem.element('article-domain')
          },
            article.hostname
          ),
          tag('div', {
            className: bem.element('article-title')
          },
            article.title
          )
        )
      )
    )
  }

  renderFilterForm() {
    return (
      tag('form', {
        className: bem.element('filter-form'),
        onSubmit: this.onCreateClick
      },
        tag('input', {
          ref: this.filterInputRef,
          className: bem.element('filter-input'),
          autoComplete: 'off',
          autoCorrect: 'off',
          autoCapitalize: 'off',
          autoFocus: true,
          disabled: !this.props.isCollectionsLoaded,
          maxLength: MAX_COLLECTION_NAME_LENGTH,
          placeholder: 'Type a name (e.g. "Places I Love") or select one below',
          spellCheck: 'false',
          tabIndex: 1,
          type: 'text',
          value: this.props.filterText,
          onChange: this.onFilterChange
        }),
        tag('button', {
          className: bem.element('filter-submit', {
            disabled: this.props.isCreateButtonDisabled,
            hidden: !this.props.isCreateButtonVisible
          }),
          disabled: this.props.isCreateButtonDisabled
        },
          'Create'
        )
      )
    )
  }

  renderCollections() {
    if (!this.props.isCollectionsLoaded) {
      return (
        tag('div', {
          className: bem.element('list', 'loading')
        })
      )
    }
    return (
      tag('div', {
        className: bem.element('list')
      },
        this.props.collections.map(item => (
          tag(CollectionListItem, {
            key: item.slug,
            className: bem.element('list-item'),
            item,
            onClick: this.onCollectionClick
          })
        ))
      )
    )
  }

  renderUnmixButton() {
    if (!this.props.isUnmixButtonVisible) {
      return null
    }
    const { isUnmixDisabled } = this.props
    return (
      tag(CapsuleButton, {
        className: bem.element('unmix-button', {
          disabled: isUnmixDisabled
        }),
        disabled: isUnmixDisabled,
        onClick: this.props.onUnmixClick
      },
        'Unmix'
      )
    )
  }

  renderInner() {
    if (this.props.isArticleFailed) {
      return this.renderInnerArticleError()
    }
    if (this.props.isCollectionsFailed) {
      return this.renderInnerCollectionsError()
    }
    if (!this.props.isArticleLoaded) {
      return (
        tag('div', {
          className: bem.element('inner', 'loading')
        })
      )
    }
    if (this.props.isUnmixPromptVisible) {
      return this.renderInnerUnmixPrompt()
    }
    return (
      tag('div', {
        className: bem.element('inner')
      },
        this.renderArticle(),
        this.renderFilterForm(),
        this.renderCollections(),
        this.renderUnmixButton()
      )
    )
  }

  renderInnerArticleError() {
    return (
      tag('div', {
        className: bem.element('inner', 'error')
      },
        tag('p', {
          className: bem.element('error-heading')
        },
          'Oops.'
        ),
        tag('p', {
          className: bem.element('error-subheading')
        },
          'We couldn’t gather the information from this website.',
          tag('br'),
          'Please double-check your link or try again later.'
        ),
        tag(CapsuleButton, {
          className: bem.element('error-button'),
          onClick: this.props.onRetryClick
        },
          'Retry'
        ),
        tag(CapsuleButton, {
          className: bem.element('error-button'),
          href: 'mailto:support@mix.com',
          onClick: this.onSupportClick
        },
          'Contact Support'
        )
      )
    )
  }

  renderInnerCollectionsError() {
    return (
      tag('div', {
        className: bem.element('inner', 'error')
      },
        tag('p', {
          className: bem.element('error-heading')
        },
          'Oops.'
        ),
        tag('p', {
          className: bem.element('error-subheading')
        },
          'Something went wrong. Give it another shot.'
        ),
        tag(CapsuleButton, {
          className: bem.element('error-button'),
          onClick: this.props.onRetryClick
        },
          'Retry'
        ),
        tag(CapsuleButton, {
          className: bem.element('error-button'),
          href: 'mailto:support@mix.com',
          onClick: this.onSupportClick
        },
          'Contact Support'
        )
      )
    )
  }

  renderInnerUnmixPrompt() {
    const { isUnmixDisabled } = this.props
    return (
      tag('div', {
        className: bem.element('inner', 'unmix')
      },
        tag('p', {
          className: bem.element('unmix-prompt')
        },
          'Do you want to remove this post from your profile?'
        ),
        tag(CapsuleButton, {
          className: bem.element('unmix-prompt-confirm'),
          disabled: isUnmixDisabled,
          onClick: this.props.onUnmixPromptConfirm
        },
          'Remove post'
        ),
        tag(CapsuleButton, {
          className: bem.element('unmix-prompt-cancel'),
          disabled: isUnmixDisabled,
          onClick: this.props.onUnmixPromptCancel
        },
          'Keep post'
        )
      )
    )
  }

  render() {
    return (
      tag(Modal, {
        className: classNames(
          this.props.className,
          bem.block()
        ),
        ariaLabel: 'Add to collection',
        onDismiss: this.props.onDismiss
      },
        this.renderHeader(),
        tag('div', {
          className: bem.element('content')
        },
          this.renderInner()
        )
      )
    )
  }
}

CollectionSelectModal.propTypes = {
  // Flags
  isArticleFailed: PropTypes.bool,
  isArticleLoaded: PropTypes.bool,
  isCreateButtonDisabled: PropTypes.bool,
  isCreateButtonVisible: PropTypes.bool,
  isCollectionsFailed: PropTypes.bool,
  isCollectionsLoaded: PropTypes.bool,
  isFilteredCollectionsEmpty: PropTypes.bool,
  isUnfilteredCollectionsEmpty: PropTypes.bool,
  isUnmixDisabled: PropTypes.bool,
  isUnmixButtonVisible: PropTypes.bool,
  isUnmixPromptVisible: PropTypes.bool,
  // Values
  className: PropTypes.string,
  article: PropTypes.shape(ArticleModelRecord.propTypes),
  collections: PropTypes.arrayOf(
    PropTypes.shape(CollectionListItemRecord.propTypes)
  ),
  filterText: PropTypes.string,
  // Callbacks
  onDismiss: PropTypes.func,
  onCreateClick: PropTypes.func,
  onCollectionClick: PropTypes.func,
  onFilterTextChange: PropTypes.func,
  onRetryClick: PropTypes.func,
  onUnmixClick: PropTypes.func,
  onUnmixPromptCancel: PropTypes.func,
  onUnmixPromptConfirm: PropTypes.func
}

module.exports = CollectionSelectModal
